import { BASE_URL } from '../../constants/baseUrl';
import axiosInstance from '../../utils/axiosInstance';
import logger from '../../utils/logger';

class VehicleService {
  /**
   * Fetches all vehicle types from the API
   * @returns {Promise} Axios response with vehicle data
   */
  fetchVehicleTypes = async () => {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/v1/lookups/vehicle-types`,
      );

      // Validate response structure
      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data.vehicles)
      ) {
        return response;
      }
      throw new Error('Invalid vehicle data structure');
    } catch (error) {
      logger.error('API Error:', error);
      throw error;
    }
  };

  /**
   * Gets a specific vehicle type by ID
   * @param {number} id - Vehicle type ID
   * @returns {Promise} Axios response
   */
  getVehicleType = (id) => {
    return axiosInstance.get(`${BASE_URL}/v1/lookups/vehicle-types/${id}`);
  };

  /**
   * Creates a new vehicle type
   * @param {FormData} vehicleFormData - Form data with vehicle details
   * @returns {Promise} Axios response
   */
  addVehicle = (vehicleFormData) => {
    // Let axiosInstance interceptor handle Content-Type for FormData
    return axiosInstance.post(
      `${BASE_URL}/v1/lookups/vehicle-types`,
      vehicleFormData,
    );
  };

  /**
   * Updates an existing vehicle type
   * @param {number} id - Vehicle type ID
   * @param {Object} queryParams - Query parameters for the update
   * @param {FormData} vehicleFormData - FormData with vehicle icon file (optional, multipart/form-data)
   * @returns {Promise} Axios response
   */
  updateVehicle = (id, queryParams, vehicleFormData = null) => {
    const params = new URLSearchParams(queryParams);
    const url = `${BASE_URL}/v1/lookups/vehicle-types/${id}?${params.toString()}`;
    const body = vehicleFormData;
    return axiosInstance.put(url, body);
  };

  /**
   * Permanently deletes a vehicle type
   * @param {number} id - Vehicle type ID
   * @returns {Promise} Axios response
   */
  deleteVehicle = (id) => {
    return axiosInstance.delete(`${BASE_URL}/v1/lookups/vehicle-types/${id}`);
  };

  /**
   * Soft deletes a vehicle type (marks as deleted)
   * @param {number} id - Vehicle type ID
   * @returns {Promise} Axios response
   */
  softDeleteVehicle = (id) => {
    return axiosInstance.delete(
      `${BASE_URL}/v1/lookups/vehicle-types/${id}/soft-delete`,
    );
  };

  /**
   * Restores a soft-deleted vehicle type
   * @param {number} id - Vehicle type ID
   * @returns {Promise} Axios response
   */
  restoreVehicle = (id) => {
    return axiosInstance.put(
      `${BASE_URL}/v1/lookups/vehicle-types/${id}/restore`,
      {},
    );
  };
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new VehicleService();
